/**
 * AI Evidence Generation Dialog.
 *
 * Opens from a "Generate with AI" button next to each evidence slot on the
 * Control Detail page. Flow:
 *   1. User picks artifact type + format (+ optional guidance) and submits.
 *   2. Backend enqueues a job, returns draft_id; we poll the draft until
 *      status flips to pending_review or failed.
 *   3. Once ready we show a preview iframe (the presigned inline URL).
 *   4. User Approves (attaches the rendered file to the evidence slot),
 *      Rejects (deletes the draft + R2 object) or Regenerates.
 */

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { Loader2, Sparkles, RefreshCw, CheckCircle2, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ARTIFACT_OPTIONS = [
  { value: 'policy',           label: 'Policy',          hint: 'A formal policy document with control sections.' },
  { value: 'procedure',        label: 'Procedure',       hint: 'Step-by-step runbook with owners and outputs.' },
  { value: 'template',         label: 'Evidence Template', hint: 'Fillable log / register / approval form.' },
  { value: 'process_diagram',  label: 'Process Description', hint: 'Textual workflow with actors and controls.' },
];

const FORMAT_OPTIONS = [
  { value: 'docx', label: 'Word (.docx)' },
  { value: 'pdf',  label: 'PDF' },
  { value: 'markdown', label: 'Markdown' },
];

// Tiny heuristic: pick a default artifact type by sniffing the requirement text.
function guessArtifactType(evidenceText) {
  const t = (evidenceText || '').toLowerCase();
  if (/template|log|register|form|sheet|record|checklist/.test(t)) return 'template';
  if (/procedure|process|runbook|steps|workflow|playbook/.test(t)) return 'procedure';
  if (/diagram|flow|architecture|topology/.test(t)) return 'process_diagram';
  return 'policy';
}

const STATUS_LABEL = {
  generating: 'Generating…',
  pending_review: 'Ready for review',
  approved: 'Approved',
  rejected: 'Rejected',
  failed: 'Failed',
};

export default function AIEvidenceDialog({
  open,
  onClose,
  auditId,
  controlId,
  evidenceIndex,
  evidenceText,
  defaultCompanyName,
  onApproved, // (file_id) => void — parent refreshes the file list
}) {
  // ---- form state ----
  const [artifactType, setArtifactType] = useState(() => guessArtifactType(evidenceText));
  const [format, setFormat] = useState('docx');
  const [audience, setAudience] = useState('');
  const [mustInclude, setMustInclude] = useState('');
  const [mustAvoid, setMustAvoid] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [companyName, setCompanyName] = useState(defaultCompanyName || '');

  // ---- run state ----
  const [submitting, setSubmitting] = useState(false);
  const [draft, setDraft] = useState(null); // {id, status, preview_url, error, ...}
  const [working, setWorking] = useState(false); // approve/reject in flight
  const pollRef = useRef(null);

  // Reset when dialog opens for a different slot.
  useEffect(() => {
    if (!open) return;
    setArtifactType(guessArtifactType(evidenceText));
    setFormat('docx');
    setAudience('');
    setMustInclude('');
    setMustAvoid('');
    setAdditionalContext('');
    setCompanyName(defaultCompanyName || '');
    setDraft(null);
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open, evidenceText, defaultCompanyName]);

  const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const startPoll = (draftId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/ai-evidence/drafts/${draftId}`, { headers: authHeader() });
        setDraft(r.data);
        if (['pending_review', 'failed', 'approved', 'rejected'].includes(r.data.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        // transient — keep polling, but stop after ~3 min
      }
    }, 2500);
  };

  const submit = async () => {
    if (!auditId || !controlId) return;
    setSubmitting(true);
    try {
      const body = {
        audit_id: auditId,
        control_id: controlId,
        evidence_index: evidenceIndex,
        artifact_type: artifactType,
        format,
        company_name: companyName.trim() || undefined,
        options: {
          audience: audience.trim() || undefined,
          must_include: mustInclude.trim() || undefined,
          must_avoid: mustAvoid.trim() || undefined,
          additional_context: additionalContext.trim() || undefined,
        },
      };
      const r = await axios.post(`${API}/ai-evidence/generate`, body, { headers: authHeader() });
      setDraft({ id: r.data.draft_id, status: 'generating' });
      startPoll(r.data.draft_id);
      toast.success('Generation started — usually completes within 20s');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start generation');
    } finally {
      setSubmitting(false);
    }
  };

  const regenerate = async () => {
    if (!draft?.id) return;
    setWorking(true);
    try {
      const body = {
        options: {
          audience: audience.trim() || undefined,
          must_include: mustInclude.trim() || undefined,
          must_avoid: mustAvoid.trim() || undefined,
          additional_context: additionalContext.trim() || undefined,
        },
        format,
        company_name: companyName.trim() || undefined,
      };
      await axios.post(`${API}/ai-evidence/drafts/${draft.id}/regenerate`, body, { headers: authHeader() });
      setDraft({ ...draft, status: 'generating', preview_url: null, error: null });
      startPoll(draft.id);
      toast.success('Regenerating…');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Regeneration failed to start');
    } finally {
      setWorking(false);
    }
  };

  const approve = async () => {
    if (!draft?.id) return;
    setWorking(true);
    try {
      const r = await axios.post(`${API}/ai-evidence/drafts/${draft.id}/approve`, {}, { headers: authHeader() });
      toast.success('Evidence attached');
      onApproved && onApproved(r.data.file_id);
      handleClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    } finally {
      setWorking(false);
    }
  };

  const reject = async () => {
    if (!draft?.id) return;
    setWorking(true);
    try {
      await axios.post(`${API}/ai-evidence/drafts/${draft.id}/reject`, {}, { headers: authHeader() });
      toast.success('Draft discarded');
      handleClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    } finally {
      setWorking(false);
    }
  };

  const handleClose = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setDraft(null);
    onClose && onClose();
  };

  const formMode = !draft || draft.status === 'rejected';
  const generatingMode = draft && draft.status === 'generating';
  const reviewMode = draft && draft.status === 'pending_review';
  const failedMode = draft && draft.status === 'failed';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="ai-evidence-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" />
            Generate evidence with AI
            {draft && (
              <Badge variant="outline" data-testid="ai-evidence-status-badge" className="ml-2">
                {STATUS_LABEL[draft.status] || draft.status}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Drafts an artifact tailored to this evidence requirement, using your company name and the
            control context. Review before attaching.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1">
          {/* Slot context */}
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm mb-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Evidence requirement</div>
            <div className="text-slate-800" data-testid="ai-evidence-source-requirement">
              {evidenceText || '(no description)'}
            </div>
          </div>

          {/* Form */}
          {formMode && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ai-artifact-type">Artifact type</Label>
                  <Select value={artifactType} onValueChange={setArtifactType}>
                    <SelectTrigger id="ai-artifact-type" data-testid="ai-artifact-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ARTIFACT_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`ai-artifact-type-${o.value}`}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-xs text-slate-500 mt-1">
                    {ARTIFACT_OPTIONS.find((o) => o.value === artifactType)?.hint}
                  </div>
                </div>
                <div>
                  <Label htmlFor="ai-format">Output format</Label>
                  <Select value={format} onValueChange={setFormat}>
                    <SelectTrigger id="ai-format" data-testid="ai-format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FORMAT_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`ai-format-${o.value}`}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="ai-company-name">Company name</Label>
                <Input
                  id="ai-company-name"
                  data-testid="ai-company-name-input"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="From your profile by default"
                />
                <div className="text-xs text-slate-500 mt-1">
                  Used in the document title, control sections and policy statement.
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ai-audience">Intended audience (optional)</Label>
                  <Input
                    id="ai-audience"
                    data-testid="ai-audience-input"
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                    placeholder="e.g. Engineering, Security team"
                  />
                </div>
                <div>
                  <Label htmlFor="ai-must-avoid">Topics to avoid (optional)</Label>
                  <Input
                    id="ai-must-avoid"
                    data-testid="ai-must-avoid-input"
                    value={mustAvoid}
                    onChange={(e) => setMustAvoid(e.target.value)}
                    placeholder="e.g. Don't reference SOC 1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="ai-must-include">Must include (optional)</Label>
                <Textarea
                  id="ai-must-include"
                  data-testid="ai-must-include-input"
                  rows={2}
                  value={mustInclude}
                  onChange={(e) => setMustInclude(e.target.value)}
                  placeholder="Specific clauses, owners, or workflows that have to appear"
                />
              </div>

              <div>
                <Label htmlFor="ai-additional-context">Additional context (optional)</Label>
                <Textarea
                  id="ai-additional-context"
                  data-testid="ai-additional-context-input"
                  rows={2}
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  placeholder="Anything else the model should know about your environment"
                />
              </div>
            </div>
          )}

          {/* Generating state */}
          {generatingMode && (
            <div className="flex flex-col items-center justify-center py-12" data-testid="ai-evidence-generating-state">
              <Loader2 className="w-8 h-8 animate-spin text-violet-600 mb-3" />
              <div className="text-sm text-slate-700">Drafting your {ARTIFACT_OPTIONS.find((o)=>o.value===artifactType)?.label.toLowerCase()}…</div>
              <div className="text-xs text-slate-500 mt-1">Usually 10–25 seconds. You can keep this dialog open or come back later via this control.</div>
            </div>
          )}

          {/* Failure state */}
          {failedMode && (
            <div className="rounded border border-rose-200 bg-rose-50 p-4 text-sm" data-testid="ai-evidence-failed-state">
              <div className="flex items-center gap-2 text-rose-700 font-medium mb-1">
                <XCircle className="w-4 h-4" /> Generation failed
              </div>
              <div className="text-rose-700">{draft?.error || 'Unknown error.'}</div>
            </div>
          )}

          {/* Preview state */}
          {reviewMode && (
            <div data-testid="ai-evidence-review-state">
              <div className="rounded border border-emerald-200 bg-emerald-50 p-3 mb-3 text-sm text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Draft ready — review the preview below, then approve to attach.
              </div>
              {draft?.format === 'markdown' ? (
                <pre
                  data-testid="ai-evidence-markdown-preview"
                  className="text-xs whitespace-pre-wrap rounded border border-slate-200 bg-white p-3 max-h-[55vh] overflow-y-auto"
                >
                  {draft.plain_text}
                </pre>
              ) : (
                <iframe
                  data-testid="ai-evidence-preview-frame"
                  src={draft?.embed_url || draft?.preview_url}
                  title="AI evidence preview"
                  className="w-full h-[55vh] rounded border border-slate-200"
                />
              )}
              <div className="text-xs text-slate-500 mt-2">
                Model: <span className="font-mono">{draft?.model}</span> · {(draft?.file_size || 0).toLocaleString()} bytes ·
                Generated: {draft?.generated_at?.slice(0, 19).replace('T',' ')}
                {draft?.preview_type === 'ms_office_online' && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-navy-700 bg-navy-50 border border-navy-200 rounded px-1.5 py-0.5">
                    MS Office Online
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-slate-200 pt-3">
          {formMode && (
            <>
              <Button variant="ghost" onClick={handleClose} data-testid="ai-evidence-cancel">Cancel</Button>
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="ai-evidence-submit"
                className="bg-violet-600 hover:bg-violet-700 text-white"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Generate
              </Button>
            </>
          )}
          {generatingMode && (
            <Button variant="ghost" onClick={handleClose} data-testid="ai-evidence-close-while-generating">
              Close (continues in background)
            </Button>
          )}
          {failedMode && (
            <>
              <Button variant="ghost" onClick={reject} disabled={working} data-testid="ai-evidence-discard">Discard</Button>
              <Button onClick={regenerate} disabled={working} data-testid="ai-evidence-retry">
                {working ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Retry
              </Button>
            </>
          )}
          {reviewMode && (
            <>
              <Button variant="ghost" onClick={reject} disabled={working} data-testid="ai-evidence-reject">
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button variant="outline" onClick={regenerate} disabled={working} data-testid="ai-evidence-regenerate">
                {working ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Regenerate
              </Button>
              <Button
                onClick={approve}
                disabled={working}
                data-testid="ai-evidence-approve"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {working ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Approve & attach
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
